import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  bodyErrors,
  bodyTargetErrors,
  errorSchema,
  guardErrors,
  idParams,
  targetErrors,
  type ErrorCode,
} from '../common';
import {
  calorieDaySchema,
  calorieDishSchema,
  calorieEntrySchema,
  calorieMealSchema,
  calorieProductSchema,
  calorieSettingsSchema,
  calorieWeightSchema,
} from '../entities';
import {
  CALORIE_DAY_VIEWS,
  INGREDIENTS_MAX,
  dailyLimitInput,
  dayInput,
  mealNameInput,
  nameInput,
  optionalGramsInput,
  optionalKcalPer100gInput,
  productNameInput,
  quantityInput,
  weightInput,
} from '../inputs';

const c = initContract();

/** `null` is "Bez sekcji". */
const mealIdInput = z.string().uuid().nullable();

/** The entry's own numbers, shared by create and edit. */
export const entryBody = z.object({
  name: productNameInput,
  kcalPer100g: optionalKcalPer100gInput,
  grams: optionalGramsInput,
  quantity: quantityInput,
});

export const createEntryBody = entryBody.extend({
  date: dayInput,
  mealId: mealIdInput,
});

/** The edit form also picks the meal; the day stays. */
export const updateEntryBody = entryBody.extend({ mealId: mealIdInput });

/** Moving an entry: another meal, another day, or both. */
export const entryPlacementBody = z.object({
  date: dayInput,
  mealId: mealIdInput,
});

/**
 * The +1 / −1 buttons. A step rather than the new value, so two quick taps
 * count twice instead of both sending the same number.
 */
export const entryStepBody = z.object({
  by: z.union([z.literal(1), z.literal(-1)]),
});

export const productBody = z.object({
  name: productNameInput,
  kcalPer100g: optionalKcalPer100gInput,
  grams: optionalGramsInput,
});

/** Logging a catalog product puts it on `date` with no meal. */
export const logProductBody = z.object({ date: dayInput });

/** One ingredient, by the same rules as a product. */
export const ingredientBody = z.object({
  name: productNameInput,
  kcalPer100g: optionalKcalPer100gInput,
  grams: optionalGramsInput,
});

/** The whole dish: its ingredients replace whatever it had, in this order. */
export const dishBody = z.object({
  name: nameInput,
  ingredients: z.array(ingredientBody).max(INGREDIENTS_MAX),
});

/** Logging a catalog dish puts one portion on `date` with no meal. */
export const logDishBody = z.object({ date: dayInput });

export const mealBody = z.object({ name: mealNameInput });

/** Every one of the user's meal ids, in the new order. */
export const mealOrderBody = z.object({
  ids: z.array(z.string().uuid()),
});

/**
 * Any of the settings alone, or several — whatever is left out stays as it
 * was. A `null` target clears it.
 */
export const calorieSettingsBody = z
  .object({
    dailyLimitKcal: dailyLimitInput.optional(),
    dayView: z.enum(CALORIE_DAY_VIEWS).optional(),
    targetWeightKg: weightInput.nullable().optional(),
  })
  .refine(
    (b) =>
      b.dailyLimitKcal !== undefined ||
      b.dayView !== undefined ||
      b.targetWeightKg !== undefined,
    { message: 'invalid_request' satisfies ErrorCode },
  );

/** One day's weight — adding it and editing it take the same two fields. */
export const weightBody = z.object({
  date: dayInput,
  weightKg: weightInput,
});

/** Parsed bodies — numbers as numbers, a blank number as `null`. */
export type EntryBody = z.infer<typeof entryBody>;
export type CreateEntryBody = z.infer<typeof createEntryBody>;
export type UpdateEntryBody = z.infer<typeof updateEntryBody>;
export type EntryPlacementBody = z.infer<typeof entryPlacementBody>;
export type ProductBody = z.infer<typeof productBody>;
export type IngredientBody = z.infer<typeof ingredientBody>;
export type DishBody = z.infer<typeof dishBody>;
export type CalorieSettingsBody = z.infer<typeof calorieSettingsBody>;
export type WeightBody = z.infer<typeof weightBody>;

/**
 * One entry's kcal, rounded per entry so a total is exactly the sum of the
 * numbers shown beside its entries. `null` while either number is missing —
 * such an entry is not counted rather than counted as zero. The server derives
 * every `kcal` in a response from this; the form uses it for its live preview.
 */
export function entryKcal(e: {
  grams: number | null;
  kcalPer100g: number | null;
  quantity: number;
}): number | null {
  if (e.grams === null || e.kcalPer100g === null) return null;
  return Math.round((e.quantity * e.grams * e.kcalPer100g) / 100);
}

/**
 * A dish's weight and kcal/100g, from its ingredients: the weight is their sum,
 * kcal/100g their weight-weighted average, rounded. A missing weight leaves
 * both unknown; a missing kcal/100g only the latter — never a total of just
 * the complete ingredients, which would read low. The server derives every
 * dish's numbers and every logged dish from this; the form uses it for its
 * live preview.
 */
export function dishTotals(
  ingredients: readonly { grams: number | null; kcalPer100g: number | null }[],
): { grams: number | null; kcalPer100g: number | null; incomplete: boolean } {
  const incomplete =
    ingredients.length === 0 ||
    ingredients.some((i) => i.grams === null || i.kcalPer100g === null);
  if (ingredients.length === 0 || ingredients.some((i) => i.grams === null))
    return { grams: null, kcalPer100g: null, incomplete };
  const grams = ingredients.reduce((sum, i) => sum + (i.grams ?? 0), 0);
  if (incomplete) return { grams, kcalPer100g: null, incomplete };
  const kcal = ingredients.reduce(
    (sum, i) => sum + ((i.grams ?? 0) * (i.kcalPer100g ?? 0)) / 100,
    0,
  );
  return { grams, kcalPer100g: Math.round((kcal / grams) * 100), incomplete };
}

/** A name already in the catalog — a product's or a dish's — whatever its case. */
const duplicateProduct = { 409: errorSchema } as const;

export const caloriesContract = c.router(
  {
    /** An empty day is a 200 with empty sections, not a 404. */
    getDay: {
      method: 'GET',
      path: '/days/:date',
      pathParams: z.object({ date: dayInput }),
      responses: { 200: calorieDaySchema, ...bodyErrors },
    },

    /** A name not yet in the catalog is added to it as well. */
    createEntry: {
      method: 'POST',
      path: '/entries',
      body: createEntryBody,
      responses: { 201: calorieEntrySchema, ...bodyTargetErrors },
    },

    /** Same catalog and meal rules as `createEntry`; the day stays as it is. */
    updateEntry: {
      method: 'PATCH',
      path: '/entries/:id',
      pathParams: idParams,
      body: updateEntryBody,
      responses: { 200: calorieEntrySchema, ...bodyTargetErrors },
    },

    /** 400 when the step would take the quantity to zero or past the maximum. */
    stepEntry: {
      method: 'POST',
      path: '/entries/:id/step',
      pathParams: idParams,
      body: entryStepBody,
      responses: { 200: calorieEntrySchema, ...bodyTargetErrors },
    },

    moveEntry: {
      method: 'PUT',
      path: '/entries/:id/placement',
      pathParams: idParams,
      body: entryPlacementBody,
      responses: { 200: calorieEntrySchema, ...bodyTargetErrors },
    },

    deleteEntry: {
      method: 'DELETE',
      path: '/entries/:id',
      pathParams: idParams,
      responses: { 204: c.noBody(), ...targetErrors },
    },

    /** A–Z. */
    listProducts: {
      method: 'GET',
      path: '/products',
      responses: { 200: z.array(calorieProductSchema), ...guardErrors },
    },

    createProduct: {
      method: 'POST',
      path: '/products',
      body: productBody,
      responses: {
        201: calorieProductSchema,
        ...bodyErrors,
        ...duplicateProduct,
      },
    },

    /** Entries already logged from this product keep their own copy. */
    updateProduct: {
      method: 'PATCH',
      path: '/products/:id',
      pathParams: idParams,
      body: productBody,
      responses: {
        200: calorieProductSchema,
        ...bodyTargetErrors,
        ...duplicateProduct,
      },
    },

    deleteProduct: {
      method: 'DELETE',
      path: '/products/:id',
      pathParams: idParams,
      responses: { 204: c.noBody(), ...targetErrors },
    },

    /** One of the product on `date`, outside any meal. */
    logProduct: {
      method: 'POST',
      path: '/products/:id/log',
      pathParams: idParams,
      body: logProductBody,
      responses: { 201: calorieEntrySchema, ...bodyTargetErrors },
    },

    /** A–Z, each with its ingredients in order. */
    listDishes: {
      method: 'GET',
      path: '/dishes',
      responses: { 200: z.array(calorieDishSchema), ...guardErrors },
    },

    createDish: {
      method: 'POST',
      path: '/dishes',
      body: dishBody,
      responses: {
        201: calorieDishSchema,
        ...bodyErrors,
        ...duplicateProduct,
      },
    },

    /** The whole dish; entries already logged from it keep their own copy. */
    updateDish: {
      method: 'PUT',
      path: '/dishes/:id',
      pathParams: idParams,
      body: dishBody,
      responses: {
        200: calorieDishSchema,
        ...bodyTargetErrors,
        ...duplicateProduct,
      },
    },

    deleteDish: {
      method: 'DELETE',
      path: '/dishes/:id',
      pathParams: idParams,
      responses: { 204: c.noBody(), ...targetErrors },
    },

    /** One portion of the dish on `date`, outside any meal, as a plain entry. */
    logDish: {
      method: 'POST',
      path: '/dishes/:id/log',
      pathParams: idParams,
      body: logDishBody,
      responses: { 201: calorieEntrySchema, ...bodyTargetErrors },
    },

    /** In the user's order. */
    listMeals: {
      method: 'GET',
      path: '/meals',
      responses: { 200: z.array(calorieMealSchema), ...guardErrors },
    },

    /** Added at the end of the order. */
    createMeal: {
      method: 'POST',
      path: '/meals',
      body: mealBody,
      responses: { 201: calorieMealSchema, ...bodyErrors },
    },

    renameMeal: {
      method: 'PATCH',
      path: '/meals/:id',
      pathParams: idParams,
      body: mealBody,
      responses: { 200: calorieMealSchema, ...bodyTargetErrors },
    },

    /** 400 unless `ids` is exactly the user's meals — a stale list is refused. */
    reorderMeals: {
      method: 'PUT',
      path: '/meals/order',
      body: mealOrderBody,
      responses: { 200: z.array(calorieMealSchema), ...bodyErrors },
    },

    /** The meal's entries stay, as "Bez sekcji". */
    deleteMeal: {
      method: 'DELETE',
      path: '/meals/:id',
      pathParams: idParams,
      responses: { 204: c.noBody(), ...targetErrors },
    },

    /** Every measurement, newest first. */
    listWeights: {
      method: 'GET',
      path: '/weights',
      responses: { 200: z.array(calorieWeightSchema), ...guardErrors },
    },

    /** Replaces the day's weight if it already has one. No future days. */
    saveWeight: {
      method: 'PUT',
      path: '/weights',
      body: weightBody,
      responses: { 200: calorieWeightSchema, ...bodyErrors },
    },

    /** A 409 when the new date already has another measurement. */
    updateWeight: {
      method: 'PATCH',
      path: '/weights/:id',
      pathParams: idParams,
      body: weightBody,
      responses: {
        200: calorieWeightSchema,
        ...bodyTargetErrors,
        409: errorSchema,
      },
    },

    deleteWeight: {
      method: 'DELETE',
      path: '/weights/:id',
      pathParams: idParams,
      responses: { 204: c.noBody(), ...targetErrors },
    },

    getSettings: {
      method: 'GET',
      path: '/settings',
      responses: { 200: calorieSettingsSchema, ...guardErrors },
    },

    /** Creates the user's row on the first save. */
    updateSettings: {
      method: 'PUT',
      path: '/settings',
      body: calorieSettingsBody,
      responses: { 200: calorieSettingsSchema, ...bodyErrors },
    },
  },
  { pathPrefix: '/calories' },
);
